/**
 * Дескрипторы CRM-сущностей для Activity (партнёр / лид / сделка).
 *
 * Регистрируются в реестре расширений Activity из `registerCrmActivityExtensions`.
 * Ядро Activity про CRM ничего не знает — вся CRM-специфика (резолв uuid→id,
 * вьюхи карточек, форматы scope-лейблов) живёт здесь.
 */
import logger from '@ayocore/platform/common/helpers/logger';
import { CRM_ACTIVITY_ENTITY_TYPES } from '@ayocore/activity/common/activity/constants';
import type {
  ActivityEntityDescriptor,
  ActivityEntityRef,
  ActivityUiHelpers,
} from '@ayocore/activity/client/extensions';
import {
  dealRepository,
  leadRepository,
  partnerRepository,
} from '@ayocore/erp/crm/client/rpc';
import { DealView } from '../components/Deals/views/DealView';
import { LeadView } from '../components/Leads/views/LeadView';
import { PartnerView } from '../components/Partners/views/PartnerView';
import { ActiveDealsChatHeaderMeta, ActiveDealsThreadListMeta } from './ActiveDealsMeta';

type EntityLookupRow = {
  id?: number | null;
};

/** Кэш резолва `entity_type:uuid → id` на время жизни клиента. */
const entityIdCache = new Map<string, number>();

async function resolveEntityId(
  ref: ActivityEntityRef,
  lookup: (uuid: string) => Promise<EntityLookupRow | null>
): Promise<number | null> {
  const cacheKey = `${ref.entity_type}:${ref.entity_uuid}`;
  const cached = entityIdCache.get(cacheKey);
  if (cached && cached > 0) return cached;

  const row = await lookup(ref.entity_uuid);
  const resolvedId = typeof row?.id === 'number' && row.id > 0 ? row.id : null;
  if (resolvedId) {
    entityIdCache.set(cacheKey, resolvedId);
  }
  return resolvedId;
}

/**
 * Общий сценарий открытия CRM-карточки по ссылке из Activity:
 * резолв uuid→id и открытие вьюхи в drawer.
 */
async function openCrmEntity(
  ref: ActivityEntityRef,
  helpers: ActivityUiHelpers,
  lookup: (uuid: string) => Promise<EntityLookupRow | null>,
  renderView: (entityId: number) => React.ReactNode
): Promise<void> {
  try {
    const entityId = await resolveEntityId(ref, lookup);
    if (!entityId) {
      logger.warn('[crm-activity-entities] Entity id is not resolved', {
        entity_type: ref.entity_type,
        entity_uuid: ref.entity_uuid,
      });
      return;
    }
    helpers.openDrawer(renderView(entityId));
  } catch (error) {
    logger.error('[crm-activity-entities] Failed to open entity', {
      entity_type: ref.entity_type,
      entity_uuid: ref.entity_uuid,
      error,
    });
  }
}

export const partnerActivityEntity: ActivityEntityDescriptor = {
  entityType: CRM_ACTIVITY_ENTITY_TYPES.PARTNER,
  label: 'Партнер',
  openEntity: (ref, helpers) =>
    openCrmEntity(
      ref,
      helpers,
      uuid => partnerRepository.findById(uuid),
      partnerId => <PartnerView partnerId={partnerId} isModal showEventList={false} />
    ),
  ThreadListMeta: ActiveDealsThreadListMeta,
  ChatHeaderMeta: ActiveDealsChatHeaderMeta,
};

export const leadActivityEntity: ActivityEntityDescriptor = {
  entityType: CRM_ACTIVITY_ENTITY_TYPES.LEAD,
  label: 'Лид',
  openEntity: (ref, helpers) =>
    openCrmEntity(
      ref,
      helpers,
      uuid => leadRepository.findById(uuid),
      leadId => <LeadView leadId={leadId} isModal showEventList={false} />
    ),
};

export const dealActivityEntity: ActivityEntityDescriptor = {
  entityType: CRM_ACTIVITY_ENTITY_TYPES.DEAL,
  label: 'Сделка',
  openEntity: (ref, helpers) =>
    openCrmEntity(
      ref,
      helpers,
      uuid => dealRepository.findById(uuid),
      dealId => <DealView dealId={dealId} isModal showEventList={false} />
    ),
  formatScopeBadge: scopeLabel => {
    const normalized = scopeLabel.startsWith('#') ? scopeLabel : `#${scopeLabel}`;
    return `Сделка ${normalized}`;
  },
  formatScopeTitle: scopeLabel => {
    const raw = scopeLabel?.trim();
    if (!raw) return 'Сделка';
    const normalized = raw.startsWith('#') ? raw : `#${raw}`;
    return `Сделка ${normalized}`;
  },
  getInlineMarker: scopeLabel => {
    const raw = scopeLabel?.trim();
    if (!raw) return '';
    return raw.startsWith('#') ? raw : `#${raw}`;
  },
};
